/**
 * Read-only readiness for a Sandboxes v2 environment's default-branch target:
 * `GET /org/sandboxes-v2/environments/:environmentId/readiness`. Never
 * triggers a build — see the route's doc comment in
 * `apps/api/src/sandboxes-v2/routes.ts`.
 *
 * @module hooks/useSandboxV2EnvironmentReadiness
 */
import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

export type SandboxV2EnvironmentReadiness = {
  status: 'ready' | 'warming' | 'stale' | 'none';
  /** The snapshot's version: a commit SHA (GitHub) or a push id (uploaded images). Set for `status === 'ready'` or `'stale'`. */
  version: string | null;
  /** When the periodic refresh last confirmed `version` as the default branch head. */
  defaultBranchAt: string | null;
  /** The last build's failure, when `status === 'none'` and one was attempted. */
  error: string | null;
};

/** 7 chars for a 40-hex commit SHA, 8 for a push id (uuid). */
export function shortSandboxV2Version(version: string | null): string {
  if (!version) return '—';
  return version.slice(0, /^[0-9a-f]{40}$/i.test(version) ? 7 : 8);
}

export function useSandboxV2EnvironmentReadiness(
  environmentId: string | null,
  enabled: boolean
) {
  return useQuery({
    queryKey: ['sandbox-v2-environment-readiness', environmentId],
    queryFn: async (): Promise<SandboxV2EnvironmentReadiness> => {
      const res = await fetch(
        `${API_URL}/org/sandboxes-v2/environments/${environmentId}/readiness`,
        { credentials: 'include' }
      );
      if (!res.ok) throw await toApiError(res, 'Failed to read Sandboxes v2 readiness');
      const json = (await res.json()) as { data: SandboxV2EnvironmentReadiness };
      return json.data;
    },
    enabled: enabled && !!environmentId,
    // Warming/none poll faster so the toolbar catches up once a build lands;
    // ready barely changes (the periodic refresh runs every few hours).
    refetchInterval: (query) => (query.state.data?.status === 'ready' ? 60_000 : 10_000),
  });
}
